import { makeStyles } from "tss-react/mui";
import { MiBadge, MiCard, MiChip } from "@miplatform/components";
import { DATE_TIME_FORMATS, HOME_PHASE } from "@miplatform/constants";
import { getFormattedDate } from "@miplatform/utils";
import { mdiHomeRoof, mdiMagnify } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { getHomePhase } from "../../../utils/getHomePhase/getHomePhase";

const hideScrollbar = {
  "&::-webkit-scrollbar": { display: "none" },
};

const centerStyle = {
  display: "flex",
  alignItems: "center",
};

export function HomeInformation({
  home = {},
  onRightIconClick = () => {},
  rightComponent = null,
}) {
  const { address = {}, builder, community, isVIP, isATakeover } = home;
  const homePhase = getHomePhase(home);

  const { classes, theme } = useStyles();

  const HOME_PHASE_MAP = {
    [HOME_PHASE.UNDER_CONSTRUCTION]: {
      text: "Under Construction",
      color: "grey",
    },
    [HOME_PHASE.UNDER_WARRANTY]: {
      text: `Under Warranty Until: ${getFormattedDate(
        DATE_TIME_FORMATS.FORMAT_MM_DD_YYYY,
        home.warrantyEndDate
      )}`,
      color: "success",
    },
    [HOME_PHASE.OUT_OF_WARRANTY]: { text: "Out of Warranty", color: "error" },
  };

  const getHomeChip = () => {
    const { text, color } = HOME_PHASE_MAP[homePhase];
    return (
      <MiChip
        color={color}
        label={text}
        size="small"
        sx={{ borderRadius: 1 }}
      />
    );
  };

  return (
    <MiCard
      title="Home Information"
      leftIcon={mdiHomeRoof}
      rightIcon={onRightIconClick && mdiMagnify}
      onRightIconClick={onRightIconClick}
      rightComponent={rightComponent}
      data-testid="home-information"
      containerStyles={{ padding: theme.spacing(2) }}
    >
      <Box className={classes.homeDetails}>
        <Box className={classes.mainInfo}>
          <Box style={centerStyle} flexWrap="wrap">
            <Typography
              fontWeight={theme.fontWeight.bold}
              className={classes.mainAddress}
            >
              {address.addressLine1}
            </Typography>
            {builder && (
              <Typography
                className={classes.borderLeft}
                fontWeight={theme.fontWeight.bold}
              >
                {builder}
              </Typography>
            )}
            {isATakeover && (
              <MiBadge
                backgroundColor={theme.palette.orange.main}
                text="Takeover"
                className={classes.takeoverBadge}
              />
            )}
          </Box>
          <Box>{getHomeChip()}</Box>
        </Box>
        <Box className={classes.extraInfo} justifyContent="space-between">
          <Box className={classes.extraInfo}>
            {community && (
              <Typography className={classes.borderRight}>
                {community}
              </Typography>
            )}
            <Typography className={classes.borderRight}>
              {address.city}, {address.regionCode}
            </Typography>
            <Typography>{address.postalCode}</Typography>
          </Box>
          <Box style={centerStyle}>
            {home.closingDate && (
              <Typography>
                {`Closing Date: ${getFormattedDate(
                  DATE_TIME_FORMATS.FORMAT_MM_DD_YYYY,
                  home.closingDate
                )}`}
              </Typography>
            )}
            {isVIP && (
              <Typography className={classes.borderLeft}>VIP</Typography>
            )}
          </Box>
        </Box>
      </Box>
    </MiCard>
  );
}

const useStyles = makeStyles()((theme) => {
  return {
    homeDetails: {
      display: "flex",
      flexDirection: "column",
      backgroundColor: theme.palette.common.white,
      borderRadius: 10,
      padding: theme.spacing(2),
      whiteSpace: "nowrap",
      justifyContent: "space-between",
    },
    mainInfo: {
      ...hideScrollbar,
      display: "flex",
      justifyContent: "space-between",
      overflowX: "auto",
      flexWrap: "wrap",
    },
    extraInfo: {
      ...centerStyle,
      ...hideScrollbar,
      color: theme.palette.bluegrey.main,
      overflowX: "auto",
      flexWrap: "wrap",
    },
    borderLeft: {
      lineHeight: 1,
      paddingLeft: theme.spacing(1.5),
      marginLeft: theme.spacing(1.5),
      borderLeft: `1px solid ${theme.palette.bluegrey.main}`,
    },
    borderRight: {
      lineHeight: 1,
      paddingRight: theme.spacing(1.5),
      borderRight: `1px solid ${theme.palette.bluegrey.main}`,
      marginRight: theme.spacing(1.5),
    },
    takeoverBadge: {
      height: theme.spacing(5),
      alignItems: "center",
      marginLeft: theme.spacing(2),
    },
    mainAddress: {
      display: "flex",
      alignItems: "center",
    },
  };
});
